import React, { useMemo } from 'react';

import { useModal } from '@stores';
import { useNprRequestHandler } from '@api/services/npr/hooks/useNprRequestHandler';
import { useNonPcnRequestHandler } from '@api/services/non-pcn/hooks/useNonPcnRequestHandler';
import { RequestType } from '@api/services/request/types';
import { useUpdateHelpInquiryStatusMutation } from '@api/services/help/hooks';
import { queryClient } from '@api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@api/services/dashboard/query';

import { HelpRequestStatuses, RequestStatus } from '@utils/enums';

import ActionMenu, { ActionMenuItem } from '@components/action-menu/ActionMenu';
import ActionMenuButton from '@components/action-menu/ActionMenuButton';
import Icon from '@components/icon/Icon';

import HelpRequestCloseModal from './HelpRequestCloseModal';
import HelpRequestSummaryModal from './HelpRequestSummaryModal';
import UnclaimHelpRequestModal from './UnclaimHelpRequestModal';
import { handleEditRequestAction, type HandleEditClickMethods } from '@pages/Dashboard/dashboard.helpers';

import {
  mdiCancel as cancelIcon,
  mdiEyeOutline as summaryIcon,
  mdiFileEyeOutline as viewIcon,
  mdiBriefcasePlusOutline as claimIcon,
  mdiBriefcaseRemoveOutline as unclaimIcon,
} from '@mdi/js';
import '@pages/dashboard/dashboard.scss';
import '@forms/components/gridCellRenderers/cellRenderers.scss';
import { HelpInquiryStatus, UpdateHelpInquiryStatusParams } from '@src/api/services/help/types';

const actionIconSize = '1.25rem';

type MenuItem = {
  id: string;
  label: React.ReactNode;
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  visible?: boolean;
};

type BuildMenuItemsParams = {
  district: string;
  helpInquiryId: number;
  methods: HandleEditClickMethods & {
    openModal: (content: React.ComponentType<any>, props: object | null) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
    updateHelpInquiryStatus: (params: UpdateHelpInquiryStatusParams) => void;
  };
  projectId: number;
  requestId: number;
  requestStatus: string;
  requestType: RequestType;
  status: HelpInquiryStatus;
  version: number;
};

const buildMenuItems = ({
  district,
  helpInquiryId,
  methods,
  projectId,
  requestId,
  requestStatus,
  requestType,
  status,
  version,
}: BuildMenuItemsParams): MenuItem[] => {
  const { openModal, updateHelpInquiryStatus } = methods;

  const handleCloseHelpRequest = () => {
    updateHelpInquiryStatus({
      helpInquiryId,
      requestId,
      version,
      status: HelpRequestStatuses.Completed,
    });
  };

  const handleUnclaimHelpRequest = () => {
    updateHelpInquiryStatus({
      helpInquiryId,
      requestId,
      version,
      status: HelpRequestStatuses.Open,
    });
  };

  if (requestStatus === RequestStatus.Cancelled) return [];

  const editClickProps = {
    district,
    methods: {
      fetchNpr: methods.fetchNpr,
      fetchNonPcn: methods.fetchNonPcn,
    },
    projectId,
    requestId,
    requestStatus,
    requestType,
    version,
  };

  const items = [
    {
      id: 'view-help-request-summary',
      label: (
        <>
          <Icon focusable={false} path={summaryIcon} size={actionIconSize} />
          View Summary
        </>
      ),
      onClick: () =>
        openModal(HelpRequestSummaryModal, {
          requestId,
          version,
          helpInquiryId,
        }),
      visible: true,
    },
    {
      id: 'view-request',
      label: (
        <>
          <Icon focusable={false} path={viewIcon} size={actionIconSize} />
          View Request
        </>
      ),
      onClick: () => handleEditRequestAction?.(editClickProps),
      visible: status === HelpRequestStatuses.InProgress,
    },
    {
      id: 'claim-help-inquiry',
      label: (
        <>
          <Icon focusable={false} path={claimIcon} size={actionIconSize} />
          Claim Help Inquiry
        </>
      ),
      onClick: (event?: React.MouseEvent<HTMLButtonElement>) => {
        updateHelpInquiryStatus({
          helpInquiryId,
          requestId,
          version,
          status: HelpRequestStatuses.InProgress,
        });
        const menuContainer = event?.currentTarget.closest('.action-menu-container');

        // force close the menu after clicking the claim button
        menuContainer?.classList.add('force-close');
        setTimeout(() => {
          menuContainer?.classList.remove('force-close');
        }, 100);
      },
      visible: status === HelpRequestStatuses.Open,
    },
    {
      id: 'unclaim-help-inquiry',
      label: (
        <>
          <Icon focusable={false} path={unclaimIcon} size={actionIconSize} />
          Unclaim Help Inquiry
        </>
      ),
      onClick: () =>
        openModal(UnclaimHelpRequestModal, {
          onUnclaim: handleUnclaimHelpRequest,
          data: { helpInquiryId },
        }),
      visible: status === HelpRequestStatuses.InProgress,
    },
    {
      id: 'close-help-inquiry',
      label: (
        <>
          <Icon focusable={false} path={cancelIcon} size={actionIconSize} />
          Complete Help Inquiry
        </>
      ),
      onClick: () =>
        openModal(HelpRequestCloseModal, {
          onClose: handleCloseHelpRequest,
          data: { helpInquiryId },
        }),
      visible: status !== HelpRequestStatuses.Completed && status !== HelpRequestStatuses.Closed,
    },
  ];
  return items.filter((item) => item.visible);
};

type HelpRequestActionsProps = {
  district: string;
  helpInquiryId: number;
  projectId: number;
  requestId: number;
  requestStatus: string;
  requestType: RequestType;
  status: HelpInquiryStatus;
  version: number;
};

const HelpRequestActions: React.FC<HelpRequestActionsProps> = ({
  district,
  helpInquiryId,
  projectId,
  requestId,
  requestStatus,
  requestType,
  status,
  version,
}) => {
  const { openModal } = useModal();
  const { fetchNpr } = useNprRequestHandler();
  const { fetchNonPcn } = useNonPcnRequestHandler();
  const { mutate: updateHelpInquiryStatus } = useUpdateHelpInquiryStatusMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_REGULATOR_HELP_REQUESTS] });
    },
  });

  const menuItems = useMemo(
    () =>
      buildMenuItems({
        district,
        helpInquiryId,
        status,
        methods: {
          fetchNpr,
          fetchNonPcn,
          openModal,
          updateHelpInquiryStatus,
        },
        projectId,
        requestId,
        requestStatus,
        requestType,
        version,
      }),
    [
      district,
      fetchNonPcn,
      fetchNpr,
      helpInquiryId,
      openModal,
      projectId,
      requestId,
      requestStatus,
      requestType,
      status,
      updateHelpInquiryStatus,
      version,
    ]
  );

  return (
    <ActionMenu items={menuItems as unknown as ActionMenuItem[]}>
      <ActionMenuButton />
    </ActionMenu>
  );
};
export default HelpRequestActions;
